import { create } from 'zustand';
import type { Campaign, GlobalSettings } from '@pages/setup/types';
import { ALIGNMENT_CAMPAIGNS, ALIGNMENT_GLOBAL } from '@pages/setup/fixtures';
import { apiFetch } from '../lib/apiClient';

export type CampaignsSource = 'fixtures' | 'portal' | 'fixtures-fallback';

type OverrideMap = Record<string, Partial<Campaign>>;

interface SetupCampaignsState {
  /** Excel-derived defaults (the canonical state from SharePoint). */
  baseCampaigns: Campaign[];
  /** Per-user overrides loaded from /api/setup/overrides — id -> partial. */
  overrides: OverrideMap;
  /** Merged campaigns (baseCampaigns + overrides) — what the UI renders. */
  campaigns: Campaign[];
  /** Site-wide settings (watermark, users, brand) from 00 Global Setup.xlsx. */
  global: GlobalSettings;
  source: CampaignsSource;
  rootPath: string | undefined;
  hasOverrides: boolean;
  /**
   * Pinned "focus" campaign — the master filter for every program-scoped page
   * (Distribution, Members & Eligibility, Predictive, Clinical,
   * Communications/Results, Financial). Flight Deck ignores this and renders
   * cards for every active campaign per spec §3.
   *
   * Default: first active campaign on hydrate. Auto-reassigned when the
   * selected campaign is unchecked. `null` only when no campaign is active.
   */
  selectedCampaignId: string | null;
  /**
   * #24 — Surfaces failures from hydrateFromPortal so the AppShell can show a
   * banner instead of the prior silent early-bail on !res.ok. null when the
   * last hydrate succeeded (or hasn't run yet); a short user-readable string
   * when the most recent attempt failed.
   */
  hydrationError: string | null;
  /** Imperative clear — used by the AppShell banner's dismiss control. */
  clearHydrationError: () => void;
  setCampaigns: (next: Campaign[], source?: CampaignsSource, rootPath?: string) => void;
  /** Toggle a checkbox or rename a nickname. Persists to per-user overrides JSON. */
  updateCampaign: (id: string, patch: Partial<Campaign>) => void;
  /** Clear all per-user overrides — snaps back to Excel defaults. */
  resetOverrides: () => Promise<void>;
  /** Local reset (without API call) — used by Reset to defaults button before overrides loaded. */
  resetToDefaults: () => void;
  hydrateFromPortal: () => Promise<void>;
  loadOverrides: () => Promise<void>;
  /**
   * Pin the given campaign as the focus. Caller is responsible for navigating
   * the URL — Header chip click handles both store + navigate; URL-driven
   * pages call this from their useSyncSelectedFromUrl hook (URL is canonical).
   */
  selectCampaign: (id: string | null) => void;
}

function applyOverrides(base: Campaign[], overrides: OverrideMap): Campaign[] {
  return base.map((c) => (overrides[c.id] ? { ...c, ...overrides[c.id] } : c));
}

/**
 * Resolve the selected-campaign id after the campaign set or its active flags
 * have changed. Keeps the current pick if it is still active; otherwise picks
 * the first active non-archived campaign; otherwise returns null. Board-only
 * campaigns live on the Flight Deck board only and are never the focus.
 */
function resolveSelected(
  current: string | null,
  campaigns: Campaign[],
): string | null {
  const isActive = (id: string): boolean =>
    campaigns.some((c) => c.id === id && c.active && !c.archived && !c.boardOnly);
  if (current && isActive(current)) return current;
  const first = campaigns.find((c) => c.active && !c.archived && !c.boardOnly);
  return first ? first.id : null;
}

let _saveTimer: ReturnType<typeof setTimeout> | null = null;
function debouncedSave(getOverrides: () => OverrideMap) {
  if (_saveTimer) clearTimeout(_saveTimer);
  _saveTimer = setTimeout(async () => {
    try {
      await apiFetch('/api/setup/overrides', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(getOverrides()),
      });
    } catch (err) {
      console.warn('[useSetupCampaigns] save overrides failed:', err);
    }
  }, 500);
}

/**
 * Shared campaign state for the /setup workspace.
 *
 * Lifted out of the Setup page so the Header chip strip + "Active in Header" KPI
 * react live to checkbox toggles. Until the file-based backend exists, this
 * seeds from `fixtures.ts`; later, it will hydrate from `/api/setup/campaigns`.
 */
export const useSetupCampaigns = create<SetupCampaignsState>((set, get) => ({
  baseCampaigns: ALIGNMENT_CAMPAIGNS,
  overrides: {},
  campaigns: ALIGNMENT_CAMPAIGNS,
  global: ALIGNMENT_GLOBAL,
  source: 'fixtures',
  rootPath: undefined,
  hasOverrides: false,
  selectedCampaignId: resolveSelected(null, ALIGNMENT_CAMPAIGNS),
  hydrationError: null,
  clearHydrationError: () => set({ hydrationError: null }),
  setCampaigns: (next, source = 'portal', rootPath) => {
    const overrides = get().overrides;
    const campaigns = applyOverrides(next, overrides);
    set({
      baseCampaigns: next,
      campaigns,
      source,
      rootPath,
      selectedCampaignId: resolveSelected(get().selectedCampaignId, campaigns),
    });
  },
  updateCampaign: (id, patch) => {
    const s = get();
    const nextOverrides: OverrideMap = {
      ...s.overrides,
      [id]: { ...(s.overrides[id] ?? {}), ...patch },
    };
    const campaigns = applyOverrides(s.baseCampaigns, nextOverrides);
    set({
      overrides: nextOverrides,
      hasOverrides: true,
      campaigns,
      selectedCampaignId: resolveSelected(s.selectedCampaignId, campaigns),
    });
    debouncedSave(() => get().overrides);
  },
  resetOverrides: async () => {
    const base = get().baseCampaigns;
    set({
      overrides: {},
      hasOverrides: false,
      campaigns: base,
      selectedCampaignId: resolveSelected(get().selectedCampaignId, base),
    });
    try {
      await apiFetch('/api/setup/overrides', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
    } catch (err) {
      console.warn('[useSetupCampaigns] reset overrides failed:', err);
    }
  },
  resetToDefaults: () =>
    set({
      baseCampaigns: ALIGNMENT_CAMPAIGNS,
      overrides: {},
      hasOverrides: false,
      campaigns: ALIGNMENT_CAMPAIGNS,
      global: ALIGNMENT_GLOBAL,
      source: 'fixtures',
      rootPath: undefined,
      selectedCampaignId: resolveSelected(null, ALIGNMENT_CAMPAIGNS),
    }),
  hydrateFromPortal: async () => {
    // #24 — Reset error on each attempt; failures below set a user-readable
    // string so the AppShell can show a banner rather than the page silently
    // running on stale-fixture data.
    set({ hydrationError: null });
    try {
      const res = await apiFetch('/api/setup/campaigns', { credentials: 'include' });
      if (!res.ok) {
        set({
          hydrationError: `Failed to load campaigns from portal (HTTP ${res.status}). Showing built-in defaults; refresh to retry.`,
        });
        return;
      }
      const body = await res.json();
      const global: GlobalSettings = body.global && typeof body.global === 'object'
        ? body.global as GlobalSettings
        : ALIGNMENT_GLOBAL;
      // Honor whatever the portal returns, including an empty array — that
      // now means "no campaigns are configured in 00 Global Setup.xlsx"
      // (William's 2026-05-23 decision: no directory-listing fallback).
      // Only fall back to fixtures when the body has no campaigns array at all
      // (i.e., the API itself errored / returned a shape we don't recognize).
      if (Array.isArray(body.campaigns)) {
        const next = body.campaigns as Campaign[];
        const overrides = get().overrides;
        const campaigns = applyOverrides(next, overrides);
        set({
          baseCampaigns: next,
          campaigns,
          global,
          source: body.source ?? 'portal',
          rootPath: body.rootPath,
          selectedCampaignId: resolveSelected(get().selectedCampaignId, campaigns),
        });
      } else {
        set({ global, source: 'fixtures-fallback', rootPath: body.rootPath });
      }
    } catch (err) {
      console.warn('[useSetupCampaigns] hydrate from portal failed:', err);
      set({
        hydrationError:
          err instanceof Error
            ? `Could not reach portal: ${err.message}. Showing built-in defaults; refresh to retry.`
            : 'Could not reach portal. Showing built-in defaults; refresh to retry.',
      });
    }
  },
  loadOverrides: async () => {
    try {
      const res = await apiFetch('/api/setup/overrides', { credentials: 'include' });
      if (!res.ok) return;
      const body = await res.json();
      if (body && body.overrides && typeof body.overrides === 'object') {
        const overrides = body.overrides as OverrideMap;
        const hasOverrides = Object.keys(overrides).length > 0;
        const campaigns = applyOverrides(get().baseCampaigns, overrides);
        set({
          overrides,
          hasOverrides,
          campaigns,
          selectedCampaignId: resolveSelected(get().selectedCampaignId, campaigns),
        });
      }
    } catch (err) {
      console.warn('[useSetupCampaigns] load overrides failed:', err);
    }
  },
  selectCampaign: (id) => {
    // Validate against the current campaigns set; if the caller passes a
    // stale or archived id we fall through to resolveSelected.
    set({ selectedCampaignId: resolveSelected(id, get().campaigns) });
  },
}));
